import React, { useState } from 'react';
import { KatRow, KatColumn, KatAlert, KatInput, KatDropdown, KatButton, KatIcon } from '@amzn/katal-react';
import './css/TellerTab.css';
import { DropDownHelper } from 'src/components/helper/DropDownHelper';
import {
    businessQuery,
    countryQuery, createTestcase,
    currencyQuery,
    expectedResultQuery, getTellerProjects, METHOD_POST,
    operatingUnitQuery,
    paymentMethodQuery, projectQuery,
    subBusinessQuery, updateCommentApi
} from 'src/components/constants/constants';

type TellerTabProps = {
    onSaveSuccess?: () => void;
};

import { getApiBaseUrl } from 'src/config/api';
import {makeApiRequest} from "src/components/helper/apiUtils";

const TellerTab: React.FC<TellerTabProps> = ({ onSaveSuccess }) => {
    const testPilotApi = getApiBaseUrl();
    const [project, setProject] = useState<string>('');
    const [isAddingProject, setIsAddingProject] = useState<boolean>(false);
    const [description, setDescription] = useState('');
    const [supplierSite, setSupplierSite] = useState('');
    const [amount, setAmount] = useState('');
    const [business, setBusiness] = useState('');
    const [subBusiness, setSubBusiness] = useState('');
    const [country, setCountry] = useState('');
    const [operatingUnit, setOperatingUnit] = useState('');
    const [currency, setCurrency] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('');
    const [expectedResult, setExpectedResult] = useState('');
    const [errors, setErrors] = useState<any>({});
    const [alert, setAlert] = useState<{ message: string; type: 'success' | 'error' | null }>({
        message: '',
        type: null
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validateForm = () => {
        const newErrors: any = {};
        if (!project) newErrors.project = 'Project is required';
        if (!description) newErrors.description = 'Description is required';
        if (!business) newErrors.business = 'Business is required';
        if (!subBusiness) newErrors.subBusiness = 'Sub Business is required';
        if (!country) newErrors.country = 'Country is required';
        if (!operatingUnit) newErrors.operatingUnit = 'Operating Unit is required';
        if (!currency) newErrors.currency = 'Currency is required';
        if (!paymentMethod) newErrors.paymentMethod = 'Payment Method is required';
        if (!supplierSite) newErrors.supplierSite = 'Supplier Site is required';
        if (!amount) newErrors.amount = 'Amount is required';
        if (!expectedResult) newErrors.expectedResult = 'Expected Result is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!validateForm()) return;
        setIsSubmitting(true);
        const payload = {
            serviceType: 'Teller',
            project,
            description,
            supplierSite,
            amount: parseFloat(amount),
            business,
            subBusiness,
            country,
            operatingUnit,
            currency,
            paymentMethod,
            expectedResult
        };

        try {
            /*const res = await fetch(testPilotApi + createTestcase, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });*/
            const res = await makeApiRequest(testPilotApi, 'POST', createTestcase, payload);

            const data = await res.json();
            if (res.ok) {
                setAlert({ message: 'Test case created successfully!', type: 'success' });
                setErrors({});
                resetForm();
                onSaveSuccess?.();
            } else {
                setAlert({ message: `Error: ${data.message || 'Something went wrong.'}`, type: 'error' });
            }
        } catch (err) {
            setAlert({ message: 'Submission error. Please try again.', type: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setProject('');
        setDescription('');
        setSupplierSite('');
        setAmount('');
        setBusiness('');
        setSubBusiness('');
        setCountry('');
        setOperatingUnit('');
        setCurrency('');
        setPaymentMethod('');
        setExpectedResult('');
    };

    const handleProjectChange = (value: string) => {
        setIsAddingProject(value.trim().toLowerCase() === 'add new project');
        setProject(value);
    };

    return (
        <div className="overlay-wrapper">
            {isSubmitting && (
                <div className="overlay">
                    <div className="overlay-message">Creating test case…</div>
                </div>
            )}
            <form onSubmit={handleSubmit}>
                {alert.message && (
                    <KatAlert
                        header={alert.type === 'success' ? 'Success' : 'Error'}
                        description={alert.message}
                        variant={alert.type === 'success' ? 'success' : 'danger'}
                        dismissed={false}
                        persistent={true}
                    />
                )}

                <div className="test-pilot-container test-case-section">
                    <KatRow className="form-row">
                        <KatColumn xs={12}>
                            <KatAlert
                                className="alert"
                                header="Ensure appropriate PEM setup and Payment config are available to process this test payment."
                                variant="info"
                            />
                        </KatColumn>
                    </KatRow>

                    <KatRow className="form-row">
                        <KatColumn xs={12}>
                            <h4>Create New Teller Test Case</h4>
                        </KatColumn>
                    </KatRow>

                    <div className="parent-container">
                        {!isAddingProject ? (
                            <KatRow className="form-row">
                                <KatColumn xs={12}>
                                    <DropDownHelper
                                        query={projectQuery + getTellerProjects}
                                        label="Project"
                                        value={project}
                                        onChange={handleProjectChange}
                                    />
                                </KatColumn>
                            </KatRow>
                        ) : (
                            <KatRow className="form-row">
                                <KatColumn xs={12}>
                                    <KatInput
                                        label="Enter New Project"
                                        type="text"
                                        value={project}
                                        onChange={(e: KatInput.ChangeEvent) => setProject(e.currentTarget.value)}
                                        state={errors.project ? 'error' : undefined}
                                        stateLabel={errors.project}
                                    />
                                </KatColumn>
                            </KatRow>
                        )}

                        <KatRow className="form-row">
                            <KatColumn xs={12}>
                                <KatInput
                                    label="Test Description"
                                    type="text"
                                    value={description}
                                    onChange={(e: KatInput.ChangeEvent) => setDescription(e.currentTarget.value)}
                                    state={errors.description ? 'error' : undefined}
                                    stateLabel={errors.description}
                                />
                            </KatColumn>
                        </KatRow>

                        <KatRow className="form-row">
                            <KatColumn xs={6}>
                                <DropDownHelper query={businessQuery} label="Business" value={business} onChange={setBusiness} />
                            </KatColumn>
                            <KatColumn xs={6}>
                                <DropDownHelper query={subBusinessQuery} label="Sub Business" value={subBusiness} onChange={setSubBusiness} />
                            </KatColumn>
                        </KatRow>

                        <KatRow className="form-row">
                            <KatColumn xs={6}>
                                <DropDownHelper query={countryQuery} label="Payor Country" value={country} onChange={setCountry} />
                            </KatColumn>
                            <KatColumn xs={6}>
                                <DropDownHelper query={operatingUnitQuery} label="Operating Unit" value={operatingUnit} onChange={setOperatingUnit} />
                            </KatColumn>
                        </KatRow>

                        <KatRow className="form-row">
                            <KatColumn xs={6}>
                                <DropDownHelper query={currencyQuery} label="Payor Currency" value={currency} onChange={setCurrency} />
                            </KatColumn>
                            <KatColumn xs={6}>
                                <DropDownHelper query={paymentMethodQuery} label="Payment Method" value={paymentMethod} onChange={setPaymentMethod} />
                            </KatColumn>
                        </KatRow>

                        <KatRow className="form-row">
                            <KatColumn xs={6}>
                                <KatInput
                                    label="Supplier site"
                                    type="text"
                                    value={supplierSite}
                                    onChange={(e: KatInput.ChangeEvent) => setSupplierSite(e.currentTarget.value)}
                                    state={errors.supplierSite ? 'error' : undefined}
                                    stateLabel={errors.supplierSite}
                                />
                            </KatColumn>
                            <KatColumn xs={6}>
                                <KatInput
                                    label="Amount"
                                    type="text"
                                    value={amount}
                                    onChange={(e: KatInput.ChangeEvent) => setAmount(e.currentTarget.value)}
                                    state={errors.amount ? 'error' : undefined}
                                    stateLabel={errors.amount}
                                />
                            </KatColumn>
                        </KatRow>

                        <KatRow className="form-row">
                            <KatColumn xs={12}>
                                <DropDownHelper query={expectedResultQuery} label="Expected Result" value={expectedResult} onChange={setExpectedResult} />
                            </KatColumn>
                        </KatRow>

                        <KatRow className="form-row">
                            <KatColumn xs={12}>
                                <div className="button-group">
                                    <KatButton label="Cancel" variant="secondary" className="button" />
                                    <KatButton type="submit" variant="primary" className="button" label="Save" />
                                </div>
                            </KatColumn>
                        </KatRow>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default TellerTab;
